import {
  confirmSignUp,
  signUp,
  type ConfirmSignUpInput,
} from 'aws-amplify/auth';
import { FC, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { PathRoutes } from 'src/const/Routes/PathRoutes';
import { useAppDispatch } from 'src/hooks/Redux/useAppDispatch';
import { useUserLoginLogout } from 'src/hooks/UserLoginLogout/UserLoginLogout';
import { fetchUser } from 'src/redux/slices/userSlice';
import { FirstStepInput, SecondStepInput } from './const';
import { FirstStep } from './FirstStep/FirstStep';
import { SecondStep } from './SecondStep/SecondStep';

import clsx from 'clsx';
import { usersApi } from 'src/api/user';
import { Utils } from 'src/utils/utils';
import featureStyles from '../RegisterModalShared.module.scss';

interface SignUpTypeProps {
  redirectUrl?: string;
  goToLogin: () => void;
}

enum SignUpStep {
  SignupRequest = 1,
  SignupVerification = 2,
  SignupComplete = 3,
}

export const SignUpType: FC<SignUpTypeProps> = ({ redirectUrl, goToLogin }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [firstStepData, setFirstStepData] = useState<FirstStepInput | null>(
    null
  );
  const [password, setPassword] = useState('');

  const { login } = useUserLoginLogout();

  const formatPhoneNumber = (number: string) => {
    return number.replace(/\s+/g, '');
  };

  const handleSignUp: SubmitHandler<FirstStepInput> = async (
    data: FirstStepInput
  ) => {
    try {
      setLoading(true);

      const formattedPhoneNumber = Utils.formatPhone(data.phoneNumber);
      await signUp({
        username: formattedPhoneNumber,
        password: data.password,
        options: {
          userAttributes: {
            phone_number: formattedPhoneNumber,
          },
        },
      });
      setPassword(data.password);
      setFirstStepData(data);
      setStep(SignUpStep.SignupVerification);
    } catch (err: any) {
      if (err.name === 'UsernameExistsException') {
        setError('Account already exists');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignUp: SubmitHandler<SecondStepInput> = async (data) => {
    try {
      if (!firstStepData) {
        throw new Error('First step data is missing');
      }

      setLoading(true);

      const formattedPhoneNumber = formatPhoneNumber(firstStepData.phoneNumber);
      const input: ConfirmSignUpInput = {
        username: formattedPhoneNumber,
        confirmationCode: data.confirmationCode,
      };
      await confirmSignUp(input);
      const result = await login({
        username: formattedPhoneNumber,
        password,
      });

      if (result?.isSignedIn) {
        await dispatch(fetchUser());

        const user = await usersApi.userMe();
        await usersApi.updateById(user.result._id, {
          full_name: firstStepData.fullName,
        });

        navigate(redirectUrl ?? PathRoutes.RootRoute);
      } else {
        setError('Login failed after confirmation');
      }
    } catch (err: any) {
      console.error('Error occurred during sign up', err);

      if (err.name === 'CodeMismatchException') {
        setError('Invalid confirmation code');
      } else {
        setError('Error occurred trying to sign up');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case SignUpStep.SignupRequest:
        return (
          <FirstStep loading={loading} error={error} onSubmit={handleSignUp} />
        );
      case SignUpStep.SignupVerification:
        return (
          <SecondStep
            loading={loading}
            error={error}
            onSubmit={handleConfirmSignUp}
          />
        );
    }
  };

  return (
    <section className={featureStyles.content}>
      <h1 className={featureStyles.title}>Sign Up</h1>

      {renderStep()}

      <div className={featureStyles.footer}>
        Already have an account?
        <button
          className={clsx(
            featureStyles.link,
            featureStyles['link__button'],
            featureStyles['link--primary']
          )}
          onClick={goToLogin}
        >
          Login
        </button>
      </div>
    </section>
  );
};
